import React, { useState, useEffect, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  connectionService,
  connectorDriverService,
  connectorService,
  systemService,
} from '../services';
import {
  Button,
  Checkbox,
  Select,
  SelectButtons,
  FormTabs,
  formatSelectOptions,
  Icon,
  Input,
  Textarea,
  TextareaAutosize,
  TextareaCode,
  textareaCodeInitialState,
  editorStateToPlainText,
} from '../components';
import { getBatchEditValue, handleError } from '../helpers';
import { useModalDialog, useGlobalState, useMessage } from '../hooks';

// The fields the form edits as JSON in a code editor. Wherever a payload is built they have to be
// sent as JSON text: a fetched row carries them already parsed, and formData carries Draft.js
// editor state, and the endpoints JSON.parse whatever arrives.
const JSON_CODE_FIELDS = [
  'datasource_filter',
  'relationship_map',
  'virtual_relationships_exclusion_filters',
];

const _AddConnectionForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    connection_id: null,
    connection_name: '',
    connection_hostname: '',
    connection_port: '',
    connection_database: '',
    connection_description: '',
    connector_id: '',
    connector_driver_id: '',
    connection_service: '',
    connection_tenant: '',
    connection_username: '',
    connection_password: '',
    connection_password_source: 'local',
    connection_password_source_reference: '',
    connection_variant: '',
    connection_string: '',
    connection_authentication_method: '',
    is_encrypted_connection: false,
    use_custom_import_query: false,
    use_custom_relationship_query: false,
    use_custom_lineage_query: false,
    custom_import_query: '',
    custom_relationship_query: '',
    custom_lineage_query: '',
    datasource_filter: textareaCodeInitialState({}),
    relationship_map: textareaCodeInitialState({}),
    virtual_relationships_exclusion_filters: textareaCodeInitialState({}),
    virtual_relationships_is_enabled: true,
    attribute_matching_is_enabled: true,
    system_id: '',
    file_upload: { value: '', file: undefined },
    create_task: true,
  };

  const initialBatchEditItems = {
    batch_connector_id: false,
    batch_connector_driver_id: false,
    batch_system_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentSystem] = useGlobalState('currentSystem');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [connectors, setConnectors] = useState([]);
  const [connector, setConnector] = useState({});
  const [connectorDrivers, setConnectorDrivers] = useState([]);
  const [connectorDriver, setConnectorDriver] = useState();
  const [systems, setSystems] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [connectionTestResult, setConnectionTestResult] = useState({
    success: null,
    msg: '',
    detail: '',
  });
  const [testing, setTesting] = useState(false);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const [abortTestConnection, setAbortTestConnection] = useState(new AbortController());

  const { showMessage, hideMessage } = useMessage();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const location = useLocation();

  useEffect(() => {
    // Get form data
    fetchConnectors();
    fetchSystems();
    fetchFormValues();

    // Cleanup
    return () => {
      abortTestConnection.abort();
    };
  }, []);

  useEffect(() => {
    // Get additional data
    if (
      Object.keys(formValues).length > 0 &&
      connector?.connector_code &&
      formValues[connector.connector_code]
    ) {
      if (formValues[connector.connector_code].connector_driver.show) {
        fetchConnectorDrivers();
      }
    }

    // Set default values
    if (
      formValues &&
      Object.keys(formValues).length > 0 &&
      connector &&
      Object.keys(connector).length > 0
    ) {
      let tmp = {};
      Object.entries(formData).forEach(([key, value]) => {
        const ds = formValues[connector.connector_code];
        const prop = ds && (ds[key.replace('connection_', '')] || {});
        const defaultValue = !value && prop && prop?.defaultValue;
        const disabled = prop?.disabled;
        if (defaultValue && ((data.length < 1 && mode === 'add') || disabled)) {
          tmp[key] = defaultValue;
        }
      });
      setFormData(prev => {
        return { ...prev, ...tmp };
      });
    }
  }, [formValues, connector]);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData(prev => {
        return {
          ...prev,
          connection_id: mode !== 'add' ? data[0].connection_id : null,
          connection_name: data[0].connection_name || '',
          connection_hostname: data[0].connection_hostname || '',
          connection_port: data[0].connection_port || '',
          connection_database: data[0].connection_database || '',
          connection_description: data[0].connection_description || '',
          connector_id: data[0].connector_id,
          connector_driver_id: data[0].connector_driver_id,
          connection_service: data[0].connection_service || '',
          connection_tenant: data[0].connection_tenant || '',
          connection_username: data[0].connection_username || '',
          connection_password: '',
          connection_password_source: data[0].connection_password_source || 'local',
          connection_password_source_reference: data[0].connection_password_source_reference || '',
          connection_variant: data[0].connection_variant || '',
          connection_string: data[0].connection_string || '',
          connection_authentication_method: data[0].connection_authentication_method,
          is_encrypted_connection: data[0].is_encrypted_connection,
          use_custom_import_query: data[0].use_custom_import_query || false,
          use_custom_relationship_query: data[0].use_custom_relationship_query || false,
          use_custom_lineage_query: data[0].use_custom_lineage_query || false,
          custom_import_query: data[0].custom_import_query || '',
          custom_relationship_query: data[0].custom_relationship_query || '',
          custom_lineage_query: data[0].custom_lineage_query || '',
          datasource_filter: textareaCodeInitialState({
            value: JSON.stringify(data[0].datasource_filter || undefined, null, 2) || '',
          }),
          virtual_relationships_exclusion_filters: textareaCodeInitialState({
            value:
              JSON.stringify(
                data[0].virtual_relationships_exclusion_filters || undefined,
                null,
                2
              ) || '',
          }),
          relationship_map: textareaCodeInitialState({
            value: JSON.stringify(data[0].relationship_map || undefined, null, 2) || '',
          }),
          virtual_relationships_is_enabled: data[0].virtual_relationships_is_enabled ?? true,
          attribute_matching_is_enabled: data[0].attribute_matching_is_enabled ?? true,
          system_id: data[0].system_id,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        connector_id: getBatchEditValue(data, 'connector_id'),
        connector_driver_id: getBatchEditValue(data, 'connector_driver_id'),
        system_id: getBatchEditValue(data, 'system_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        system_id: location.pathname === '/connections' ? undefined : currentSystem.system_id, //Pre-select system only if created from the system view.
      });
    } else {
      resetForm();
    }

    if (data.length > 0) {
      setConnector(connectors.find(x => x.connector_id === data[0].connector_id));
      setConnectorDriver(
        connectorDrivers.find(x => x.connector_driver_id === data[0].connector_driver_id)
      );
    }
  }, [mode, data, loggedInUser, currentSystem]);

  const fetchConnectors = async () => {
    connectorService
      .getAll()
      .then(res => {
        const result = res.connectors.map(x => ({
          ...x,
          // A connector that failed to load is as unusable as a disabled one, so the dropdown
          // treats them alike and the tooltip says which of the two it is.
          connector_is_usable: x.connector_status === 'enabled',
          connector_tooltip:
            (x.connector_status === 'disabled' &&
              'This connector is disabled and cannot be used') ||
            // An error is one the api service saw while loading the connector, so its own log is
            // where the reason is. A separate worker service's connectors are not reported on here.
            (x.connector_status === 'error' &&
              'This connector failed to load and cannot be used. See the api service log for the reason.'),
        }));
        setConnectors(result);
        data[0] && setConnector(res.connectors.find(x => x.connector_id === data[0].connector_id));
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchConnectorDrivers = async () => {
    connectorDriverService
      .getByConnectorCode(connector.connector_code)
      .then(res => {
        setConnectorDrivers(res.connector_drivers);
        data[0] &&
          setConnectorDriver(
            res.connector_drivers.find(x => x.connector_driver_id === data[0].connector_driver_id)
          );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchSystems = async () => {
    systemService
      .getAll()
      .then(res => {
        setSystems(res.systems);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFormValues = async () => {
    connectionService
      .getFormValues()
      .then(res => {
        if (res) {
          const result = res.formValues;
          setFormValues(result);

          // Set default values
          let tmp = {};
          Object.entries(formData).forEach(([key, value]) => {
            const ds = result[connector.connector_code];
            const prop = ds && (ds[key.replace('connection_', '')] || {});
            const defaultValue = prop && prop?.defaultValue;
            const disabled = prop?.disabled;

            if (defaultValue && ((data.length < 1 && mode === 'add') || disabled)) {
              tmp[key] = defaultValue;
            }
          });
          setFormData(prev => {
            return { ...prev, ...tmp };
          });
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };
  const handleInputChange = (event, formPart) => {
    let { name, value, file } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;
      file = event.target.files && event.target.files[0];

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    if (name === 'connector_id') {
      setConnector(connectors.find(x => x.connector_id === value));
    }
    if (name === 'connector_driver_id') {
      setConnectorDriver(connectorDrivers.find(x => x.connector_driver_id === value));
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        switch (name) {
          case 'file_upload':
            setFormData({
              ...formData,
              connection_hostname: value
                ? value.split(/\\|\//).slice(-1)[0]
                : formData.connection_hostname,
              [name]: { value, file },
            });
            break;
          default:
            setFormData({ ...formData, [name]: value });
        }
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }

    resetConnectionTestResult();
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = {
            ...tmp,
            [key]: JSON_CODE_FIELDS.includes(key)
              ? editorStateToPlainText(formData[key]) || null
              : formData[key],
          };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          connection_id: item.connection_id,
          connection_name: item.connection_name,
          connection_hostname: item.connection_hostname,
          connection_port: item.connection_port,
          connection_database: item.connection_database,
          connector_id: item.connector_id,
          connector_driver_id: item.connector_driver_id,
          connection_service: item.connection_service,
          connection_tenant: item.connection_tenant,
          connection_username: item.connection_username,
          connection_password: item.connection_password,
          connection_password_source: item.connection_password_source,
          connection_password_source_reference: item.connection_password_source_reference,
          connection_string: item.connection_string,
          connection_variant: item.connection_variant,
          connection_authentication_method: item.connection_authentication_method,
          is_encrypted_connection: item.is_encrypted_connection,
          use_custom_import_query: item.use_custom_import_query,
          use_custom_relationship_query: item.use_custom_relationship_query,
          use_custom_lineage_query: item.use_custom_lineage_query,
          custom_import_query: item.custom_import_query,
          custom_relationship_query: item.custom_relationship_query,
          custom_lineage_query: item.custom_lineage_query,
          // Carried on the row as parsed JSON; the endpoint takes JSON text.
          datasource_filter: item.datasource_filter ? JSON.stringify(item.datasource_filter) : null,
          relationship_map: item.relationship_map ? JSON.stringify(item.relationship_map) : null,
          virtual_relationships_exclusion_filters: item.virtual_relationships_exclusion_filters
            ? JSON.stringify(item.virtual_relationships_exclusion_filters)
            : null,
          virtual_relationships_is_enabled: item.virtual_relationships_is_enabled,
          attribute_matching_is_enabled: item.attribute_matching_is_enabled,
          system_id: item.system_id,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        use_custom_import_query:
          formValues[connector.connector_code] &&
          'defaultValue' in formValues[connector.connector_code].use_custom_import_query
            ? formValues[connector.connector_code].use_custom_import_query.defaultValue
            : formData.use_custom_import_query,
        use_custom_relationship_query:
          formValues[connector.connector_code] &&
          'defaultValue' in formValues[connector.connector_code].use_custom_relationship_query
            ? formValues[connector.connector_code].use_custom_relationship_query.defaultValue
            : formData.use_custom_relationship_query,
        use_custom_lineage_query:
          formValues[connector.connector_code] &&
          'defaultValue' in formValues[connector.connector_code].use_custom_lineage_query
            ? formValues[connector.connector_code].use_custom_lineage_query.defaultValue
            : formData.use_custom_lineage_query,
        ...formData,
        datasource_filter: editorStateToPlainText(formData.datasource_filter) || null,
        relationship_map: editorStateToPlainText(formData.relationship_map) || null,
        virtual_relationships_exclusion_filters:
          editorStateToPlainText(formData.virtual_relationships_exclusion_filters) || null,
      };

      // Delete properties used for form logic only and
      // that are not supposed to be saved to database
      delete dataToSubmit.file_upload;
    }

    // Delete properties
    if (mode !== 'add') {
      if (Array.isArray(dataToSubmit)) {
        dataToSubmit.forEach(x => {
          delete x.create_task;
        });
      } else {
        delete dataToSubmit.create_task;
      }
    }

    let filesToSubmit =
      batchEdit || !formData.file_upload.file ? [] : [{ file_upload: formData.file_upload.file }];

    // submit form
    submitForm(dataToSubmit, filesToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetConnectionTestResult();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setConnector({});
    setConnectorDriver();
    setFormData(() => {
      return {
        ...initialFormState,
        system_id: currentSystem ? currentSystem.system_id : null,
      };
    });
  };

  /**
   * Clears the result of the previous connection test.
   */
  const resetConnectionTestResult = () => {
    setConnectionTestResult({ success: null, msg: '', detail: '' });
  };

  /**
   * Tests the connection as it is currently filled in, without saving it.
   *
   * The result is reported in place, under the button: the message, plus the connector's own detail
   * line under it when it supplies one.
   * @returns {Promise<void>}
   */
  const testConnection = async () => {
    resetConnectionTestResult();
    setTesting(true);

    try {
      const res = await connectionService.testConnection(
        {
          connections: [
            {
              ...formData,
              datasource_filter: editorStateToPlainText(formData.datasource_filter) || null,
              relationship_map: editorStateToPlainText(formData.relationship_map) || null,
              virtual_relationships_exclusion_filters:
                editorStateToPlainText(formData.virtual_relationships_exclusion_filters) || null,
              connector_code: connector.connector_code,
              connector_name: connector.connector_name,
            },
          ],
        },
        abortTestConnection
      );
      if (res) {
        // detail is what the connector has to report beyond having connected, and only some supply
        // one. It is shown under the message rather than replacing it.
        setConnectionTestResult({ status: res.status, msg: res.message, detail: res.detail });
      }
    } catch (err) {
      handleError({ err }, () => {
        setConnectionTestResult({
          status: 'failed',
          msg: 'Connection failed',
          detail: `${err.message}:\n ${err.error}`,
        });
      });
    } finally {
      setTesting(false);
    }
  };

  const cancelTestConnection = () => {
    abortTestConnection.abort();
    setAbortTestConnection(new AbortController());
  };

  const saveImportQuery = query => {
    setFormData(prev => {
      return {
        ...prev,
        custom_import_query: query,
      };
    });
    hideModalDialog();
  };

  const saveRelationshipQuery = query => {
    setFormData(prev => {
      return {
        ...prev,
        custom_relationship_query: query,
      };
    });
    hideModalDialog();
  };

  const saveLineageQuery = query => {
    setFormData(prev => {
      return {
        ...prev,
        custom_lineage_query: query,
      };
    });
    hideModalDialog();
  };

  /**
   * What to show under the connector field.
   *
   * A connector that is disabled or that failed to load cannot be picked here, but a connection
   * that already uses one still opens for editing -- and nothing else on the form would say why its
   * tasks are failing. Reported as 'danger' rather than as an error on the field: the connection is
   * still worth saving, the connector is what needs fixing. A validation error from the API wins,
   * and nothing is said in batch edit, where the field can stand for several connections at once.
   * @returns {{ valid: boolean|string, message: string }|undefined}
   */
  const getConnectorValidation = () => {
    if (formErrors.connector_id) {
      return { valid: false, message: formErrors.connector_id };
    }

    if (batchEdit) return undefined;

    if (connector?.connector_status === 'disabled') {
      return {
        valid: 'danger',
        message:
          'This connector is disabled in the configuration. Tasks using this connection will fail until it is enabled.',
      };
    }

    if (connector?.connector_status === 'error') {
      return {
        valid: 'danger',
        message:
          'This connector failed to load. Tasks using this connection will fail until that is fixed. See the api service log for the reason.',
      };
    }

    return undefined;
  };

  const renderConnectionString = () => {
    let str = '';

    if (!connectorDriver) {
      return '';
    }

    str += `DRIVER={${connectorDriver.connector_driver_name}};`;

    Object.entries(connectorDriver.connector_driver_settings.connectionStringMap).forEach(
      ([key, value]) => {
        if (formData[`connection_${key}`] && key !== 'password') {
          const param = value;
          const val = formData[`connection_${key}`];
          str += `${param}=${val};`;
        } else if (key === 'password' && (mode !== 'add' || formData.connection_password)) {
          const param = value;
          const val = '<hidden>';
          str += `${param}=${val};`;
        }
      }
    );

    str += formData.connection_string;

    return str;
  };

  const hasConnector = connector && formValues[connector.connector_code] ? true : false;

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="connection_name"
              label="Name"
              value={formData.connection_name}
              onChange={handleInputChange}
              autoComplete="off"
              disabled={loading}
              validation={
                formErrors.connection_name
                  ? { valid: false, message: formErrors.connection_name }
                  : undefined
              }
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_connector_id}
                name="batch_connector_id"
                label="Edit Connector"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="connector_id"
            label={!batchEdit && 'Connector'}
            value={formData.connector_id === 'multiple' ? undefined : formData.connector_id}
            options={formatSelectOptions({
              options: connectors,
              optionValue: 'connector_id',
              optionLabel: 'connector_name',
              optionIcon: 'connector_code',
              optionEnabled: 'connector_is_usable',
              tooltip: 'connector_tooltip',
            })}
            onChange={handleInputChange}
            placeholder={
              formData.connector_id === 'multiple' ? '< Multiple >' : 'Select connector...'
            }
            sortOptions={false}
            disabled={loading || (batchEdit && !batchEditItems.batch_connector_id)}
            validation={getConnectorValidation()}
          />
        </div>

        <FormTabs defaultSelectedTab={defaultSelectedTab} formErrors={formErrors}>
          <div label="general">
            {!batchEdit && (
              <div className="form-block vertical">
                <TextareaAutosize
                  label="Description"
                  name="connection_description"
                  value={formData.connection_description}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_system_id}
                    name="batch_system_id"
                    label="Edit System"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="system_id"
                label={!batchEdit && 'System'}
                value={formData.system_id === 'multiple' ? undefined : formData.system_id}
                options={formatSelectOptions({
                  options: systems,
                  optionValue: 'system_id',
                  optionLabel: 'system_name',
                  optionSecondaryLabel: 'system_code',
                  tooltip: 'system_description',
                })}
                onChange={handleInputChange}
                title="Datasource(s) synced with this connection will be ordered under the system selected here"
                placeholder={
                  formData.system_id === 'multiple' ? '< Multiple >' : 'Select system...'
                }
                disabled={loading || (batchEdit && !batchEditItems.batch_system_id)}
                validation={
                  formErrors.system_id ? { valid: false, message: formErrors.system_id } : undefined
                }
              />
            </div>

            {mode === 'add' && (
              <div className="form-block vertical">
                <Checkbox
                  name="create_task"
                  checked={formData.create_task}
                  onChange={handleInputChange}
                  label="Create Datasource Sync Task"
                  title="Automatically create a Datasource Sync Task for this connection"
                  disabled={loading}
                />
              </div>
            )}
          </div>

          {!batchEdit && hasConnector && (
            <div label="connection">
              {formValues[connector.connector_code].connector_driver.show && (
                <div className="form-block vertical">
                  {batchEdit && (
                    <label>
                      <Checkbox
                        checked={batchEditItems.batch_connector_driver_id}
                        name="batch_connector_driver_id"
                        label="Edit Driver"
                        onChange={e => handleInputChange(e, 'batch')}
                        disabled={loading}
                      />
                    </label>
                  )}
                  <Select
                    name="connector_driver_id"
                    value={
                      formData.connector_driver_id === 'multiple'
                        ? undefined
                        : formData.connector_driver_id
                    }
                    options={formatSelectOptions({
                      options: connectorDrivers,
                      optionValue: 'connector_driver_id',
                      optionLabel: 'connector_driver_name',
                      optionSecondaryLabel: 'connector_driver_datasource_type_name',
                    })}
                    onChange={handleInputChange}
                    placeholder={
                      formData.connector_driver_id === 'multiple'
                        ? '< Multiple >'
                        : formValues[connector.connector_code].connector_driver.placeholder
                    }
                    disabled={loading || (batchEdit && !batchEditItems.batch_connector_driver_id)}
                    label={formValues[connector.connector_code].connector_driver.label}
                    title={formValues[connector.connector_code].connector_driver.tooltip}
                  />
                </div>
              )}

              {Object.entries(formValues[connector.connector_code]).map(([key, field]) => {
                if (field.type) {
                  let element = <span></span>;

                  let label = field.label;
                  let tooltip = field?.tooltip && field.tooltip !== '' ? field.tooltip : undefined;
                  let placeholder = field.placeholder;
                  let disabled = field.disabled || false;
                  let accept = field.acceptFileTypes;
                  let value = formData[field.dbFieldName];
                  let options = field.options;
                  let show = field.show;

                  if (field.states) {
                    Object.entries(field.states).forEach(([key, state]) => {
                      const formDataValue =
                        (formData[key] && formData[key].toString()) ||
                        (formData[`connection_${key}`] && formData[`connection_${key}`].toString());
                      const formDataState =
                        (formDataValue && state.not_null) || state[formDataValue];

                      value = formData[formDataState?.value] || value;
                      placeholder = formDataState?.placeholder || placeholder;
                      disabled = formDataState?.disabled || disabled;
                      label = formDataState?.label || label;
                      tooltip = formDataState?.tooltip || tooltip;
                      options = formDataState?.options || options;
                      show = formDataState?.show === undefined ? show : formDataState?.show;
                    });
                  }

                  if (!show) {
                    return;
                  }

                  switch (field.type) {
                    case 'input-text':
                      element = (
                        <Input
                          type="text"
                          name={field.dbFieldName}
                          label={label}
                          value={value}
                          onChange={handleInputChange}
                          autoComplete="off"
                          disabled={loading || disabled}
                          title={tooltip}
                          placeholder={placeholder}
                          validation={
                            formErrors[field.dbFieldName]
                              ? { valid: false, message: formErrors[field.dbFieldName] }
                              : undefined
                          }
                        />
                      );
                      break;
                    case 'input-password':
                      element = (
                        <Input
                          type="password"
                          name={field.dbFieldName}
                          label={label}
                          value={value}
                          onChange={handleInputChange}
                          autoComplete="new-password"
                          disabled={loading || disabled}
                          title={tooltip}
                          placeholder={mode !== 'add' ? '< hidden >' : placeholder}
                          validation={
                            formErrors[field.dbFieldName]
                              ? { valid: false, message: formErrors[field.dbFieldName] }
                              : undefined
                          }
                        />
                      );
                      break;
                    case 'input-file':
                      element = (
                        <Input
                          type="file"
                          name={field.dbFieldName}
                          label={label}
                          value={value.value}
                          onChange={handleInputChange}
                          disabled={loading || disabled}
                          title={tooltip}
                          accept={accept}
                        />
                      );
                      break;
                    case 'select':
                      element = options && (
                        <Select
                          name={field.dbFieldName}
                          label={label}
                          value={value}
                          options={formatSelectOptions({
                            options: options,
                            optionValue: 'value',
                            optionLabel: 'label',
                            optionTooltip: 'tooltip',
                          })}
                          onChange={handleInputChange}
                          disabled={loading || disabled}
                          title={tooltip}
                          accept={accept}
                          placeholder={placeholder}
                          sortOptions={false}
                        />
                      );
                      break;
                    case 'select-buttons':
                      element = options && (
                        <SelectButtons
                          name={field.dbFieldName}
                          label={label}
                          value={value}
                          options={formatSelectOptions({
                            options: options,
                            optionValue: 'value',
                            optionLabel: 'label',
                            optionTooltip: 'tooltip',
                          })}
                          onChange={handleInputChange}
                          disabled={loading || disabled}
                          title={tooltip}
                          accept={accept}
                        />
                      );
                      break;
                    case 'checkbox':
                      element = (
                        <Checkbox
                          name={field.dbFieldName}
                          checked={value}
                          onChange={handleInputChange}
                          label={label}
                          disabled={loading || disabled}
                          title={tooltip}
                        />
                      );
                      break;
                    default:
                      break;
                  }

                  return (
                    <div key={key} className="form-block vertical">
                      {element}
                    </div>
                  );
                }
              })}

              {formValues[connector.connector_code].connection_string_preview.show === true &&
                formData.connector_driver_id && (
                  <div className="form-block vertical disabled">
                    <h3>Connection String Preview</h3>
                    <div className="form-light-text no-margin">{renderConnectionString()}</div>
                  </div>
                )}

              {/* Testing a file-based source only makes sense for a URL: when a file is selected
                  for upload there is no connection to reach, so the test is hidden. DB connectors
                  never set file_upload.file, so they always show the button. */}
              {!formData.file_upload?.file && (
                <React.Fragment>
                  <div className="form-block horizontal">
                    <Button
                      value="Test Connection"
                      className={testing ? ' loading' : ''}
                      onClick={testConnection}
                      disabled={testing || loading}
                    />

                    <div
                      className={
                        'connection-test-result' +
                        (!connectionTestResult.status ? '' : ' ' + connectionTestResult.status)
                      }
                    >
                      {testing ? (
                        <div className="connection-test-result-message">
                          <div>Connecting...</div>
                          <div className="connection-test-cancel" onClick={cancelTestConnection}>
                            Cancel
                          </div>
                        </div>
                      ) : (
                        <React.Fragment>
                          {connectionTestResult.status && (
                            <Icon name={connectionTestResult.status} />
                          )}
                          <div className="connection-test-result-message">
                            {connectionTestResult.msg}
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </div>

                  {/* Every outcome is announced beside the button, where there is room for a
                      headline and no more. What the connector had to say is a sentence, so it goes
                      under the button on a line of its own, outside that row. */}
                  {connectionTestResult.detail && (
                    <div
                      className={
                        'connection-test-result-detail' +
                        (!connectionTestResult.status ? '' : ' ' + connectionTestResult.status)
                      }
                    >
                      {connectionTestResult.detail}
                    </div>
                  )}
                </React.Fragment>
              )}
            </div>
          )}

          {!batchEdit &&
            hasConnector &&
            formValues[connector.connector_code].datasource_filter.show === true && (
              <div label="filters">
                <div className="form-block vertical">
                  <TextareaCode
                    name={formValues[connector.connector_code].datasource_filter.dbFieldName}
                    label={formValues[connector.connector_code].datasource_filter.label}
                    title={formValues[connector.connector_code].datasource_filter.tooltip}
                    value={formData.datasource_filter}
                    onChange={handleInputChange}
                    disabled={
                      loading || formValues[connector.connector_code].datasource_filter.disabled
                    }
                    language="json"
                    showLineNumbers={false}
                    expandable={true}
                    placeholder={formValues[connector.connector_code].datasource_filter.placeholder}
                    isOptional={true}
                    validation={
                      formErrors.datasource_filter
                        ? { valid: false, message: formErrors.datasource_filter }
                        : undefined
                    }
                  />
                </div>

                <div className="form-block vertical">
                  <Button
                    // Common-filter connectors (api/file/dbt) have no SQL to show — the same modal
                    // shows a validation result instead. The flag only swaps the button label.
                    value={
                      formValues[connector.connector_code].datasource_filter
                        .useCommonDatasourceFilter
                        ? 'Validate Filter'
                        : 'Show Filter Query'
                    }
                    onClick={() =>
                      showModalDialog('validateDatasourceFilter', {
                        ...formData,
                        datasource_filter:
                          editorStateToPlainText(formData.datasource_filter) || null,
                        relationship_map: editorStateToPlainText(formData.relationship_map) || null,
                        virtual_relationships_exclusion_filters:
                          editorStateToPlainText(
                            formData.virtual_relationships_exclusion_filters
                          ) || null,
                        // UI-only hint for how the modal renders the result (text vs. code editor);
                        // stripped in useModalDialog before the request, not sent to the backend.
                        useCommonDatasourceFilter:
                          formValues[connector.connector_code].datasource_filter
                            .useCommonDatasourceFilter,
                      })
                    }
                  />
                </div>
              </div>
            )}

          {!batchEdit &&
            hasConnector &&
            (formValues[connector.connector_code].use_custom_import_query.show ||
              formValues[connector.connector_code].use_custom_relationship_query.show ||
              formValues[connector.connector_code].use_custom_lineage_query.show ||
              formValues[connector.connector_code].relationship_map.show ||
              formValues[connector.connector_code].virtual_relationships_is_enabled.show ||
              formValues[connector.connector_code].attribute_matching_is_enabled.show) && (
              <div label="advanced">
                {formValues[connector.connector_code].use_custom_import_query.show && (
                  <React.Fragment>
                    <div className="form-block horizontal">
                      <Checkbox
                        name={
                          formValues[connector.connector_code].use_custom_import_query.dbFieldName
                        }
                        checked={
                          'defaultValue' in
                          formValues[connector.connector_code].use_custom_import_query
                            ? formValues[connector.connector_code].use_custom_import_query
                                .defaultValue
                            : formData.use_custom_import_query
                        }
                        onChange={handleInputChange}
                        label={formValues[connector.connector_code].use_custom_import_query.label}
                        title={formValues[connector.connector_code].use_custom_import_query.tooltip}
                        disabled={
                          loading ||
                          formValues[connector.connector_code].use_custom_import_query.disabled
                        }
                      />
                    </div>

                    {('defaultValue' in formValues[connector.connector_code].use_custom_import_query
                      ? formValues[connector.connector_code].use_custom_import_query.defaultValue
                      : formData.use_custom_import_query) && (
                      <div className="form-block vertical">
                        <Button
                          value="Edit Schema Query"
                          onClick={() =>
                            showModalDialog(
                              'editImportQuery',
                              {
                                ...formData,
                                datasource_filter:
                                  editorStateToPlainText(formData.datasource_filter) || null,
                                relationship_map:
                                  editorStateToPlainText(formData.relationship_map) || null,
                                virtual_relationships_exclusion_filters:
                                  editorStateToPlainText(
                                    formData.virtual_relationships_exclusion_filters
                                  ) || null,
                              },
                              saveImportQuery
                            )
                          }
                        />
                      </div>
                    )}
                  </React.Fragment>
                )}
                {formValues[connector.connector_code].use_custom_relationship_query.show && (
                  <React.Fragment>
                    <div className="form-block horizontal">
                      <Checkbox
                        name={
                          formValues[connector.connector_code].use_custom_relationship_query
                            .dbFieldName
                        }
                        checked={
                          'defaultValue' in
                          formValues[connector.connector_code].use_custom_relationship_query
                            ? formValues[connector.connector_code].use_custom_relationship_query
                                .defaultValue
                            : formData.use_custom_relationship_query
                        }
                        onChange={handleInputChange}
                        label={
                          formValues[connector.connector_code].use_custom_relationship_query.label
                        }
                        title={
                          formValues[connector.connector_code].use_custom_relationship_query.tooltip
                        }
                        disabled={
                          loading ||
                          formValues[connector.connector_code].use_custom_relationship_query
                            .disabled
                        }
                      />
                    </div>

                    {('defaultValue' in
                    formValues[connector.connector_code].use_custom_relationship_query
                      ? formValues[connector.connector_code].use_custom_relationship_query
                          .defaultValue
                      : formData.use_custom_relationship_query) && (
                      <div className="form-block vertical">
                        <Button
                          value="Edit Relationship Query"
                          onClick={() =>
                            showModalDialog(
                              'editRelationshipQuery',
                              {
                                ...formData,
                                datasource_filter:
                                  editorStateToPlainText(formData.datasource_filter) || null,
                                relationship_map:
                                  editorStateToPlainText(formData.relationship_map) || null,
                                virtual_relationships_exclusion_filters:
                                  editorStateToPlainText(
                                    formData.virtual_relationships_exclusion_filters
                                  ) || null,
                              },
                              saveRelationshipQuery
                            )
                          }
                        />
                      </div>
                    )}
                  </React.Fragment>
                )}
                {formValues[connector.connector_code].use_custom_lineage_query.show && (
                  <React.Fragment>
                    <div className="form-block horizontal">
                      <Checkbox
                        name={
                          formValues[connector.connector_code].use_custom_lineage_query.dbFieldName
                        }
                        checked={
                          'defaultValue' in
                          formValues[connector.connector_code].use_custom_lineage_query
                            ? formValues[connector.connector_code].use_custom_lineage_query
                                .defaultValue
                            : formData.use_custom_lineage_query
                        }
                        onChange={handleInputChange}
                        label={formValues[connector.connector_code].use_custom_lineage_query.label}
                        title={
                          formValues[connector.connector_code].use_custom_lineage_query.tooltip
                        }
                        disabled={
                          loading ||
                          formValues[connector.connector_code].use_custom_lineage_query.disabled
                        }
                      />
                    </div>

                    {('defaultValue' in
                    formValues[connector.connector_code].use_custom_lineage_query
                      ? formValues[connector.connector_code].use_custom_lineage_query.defaultValue
                      : formData.use_custom_lineage_query) && (
                      <div className="form-block vertical">
                        <Button
                          value="Edit Lineage Query"
                          onClick={() =>
                            showModalDialog(
                              'editLineageQuery',
                              {
                                ...formData,
                                datasource_filter:
                                  editorStateToPlainText(formData.datasource_filter) || null,
                                relationship_map:
                                  editorStateToPlainText(formData.relationship_map) || null,
                                virtual_relationships_exclusion_filters:
                                  editorStateToPlainText(
                                    formData.virtual_relationships_exclusion_filters
                                  ) || null,
                              },
                              saveLineageQuery
                            )
                          }
                        />
                      </div>
                    )}
                  </React.Fragment>
                )}
                {formValues[connector.connector_code].relationship_map.show && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <h4>Relationship Map</h4>
                      <TextareaCode
                        name={formValues[connector.connector_code].relationship_map.dbFieldName}
                        value={formData.relationship_map}
                        onChange={handleInputChange}
                        disabled={
                          loading || formValues[connector.connector_code].relationship_map.disabled
                        }
                        label={formValues[connector.connector_code].relationship_map.label}
                        title={formValues[connector.connector_code].relationship_map.tooltip}
                        language="json"
                        showLineNumbers={false}
                        expandable={true}
                        placeholder={
                          formValues[connector.connector_code].relationship_map.placeholder
                        }
                        isOptional={true}
                        validation={
                          formErrors.relationship_map
                            ? { valid: false, message: formErrors.relationship_map }
                            : undefined
                        }
                      />
                    </div>

                    <div className="form-block horizontal">
                      <Button
                        value="Show Relationship Upsert Query"
                        onClick={() =>
                          showModalDialog('showRelationshipQuery', {
                            ...formData,
                            relationship_map:
                              editorStateToPlainText(formData.relationship_map) || null,
                            virtual_relationships_exclusion_filters:
                              editorStateToPlainText(
                                formData.virtual_relationships_exclusion_filters
                              ) || null,
                          })
                        }
                      />
                    </div>
                  </React.Fragment>
                )}

                {formValues[connector.connector_code].virtual_relationships_is_enabled.show && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <h4>Virtual Relationships</h4>
                      <Checkbox
                        name={
                          formValues[connector.connector_code].virtual_relationships_is_enabled
                            .dbFieldName
                        }
                        checked={formData.virtual_relationships_is_enabled}
                        onChange={handleInputChange}
                        label={
                          formValues[connector.connector_code].virtual_relationships_is_enabled
                            .label
                        }
                        title={
                          formValues[connector.connector_code].virtual_relationships_is_enabled
                            .tooltip
                        }
                        disabled={
                          loading ||
                          formValues[connector.connector_code].virtual_relationships_is_enabled
                            .disabled
                        }
                      />
                    </div>

                    {formValues[connector.connector_code].virtual_relationships_exclusion_filters
                      .show &&
                      formData.virtual_relationships_is_enabled && (
                        <div className="form-block vertical">
                          <TextareaCode
                            name={
                              formValues[connector.connector_code]
                                .virtual_relationships_exclusion_filters.dbFieldName
                            }
                            value={formData.virtual_relationships_exclusion_filters}
                            onChange={handleInputChange}
                            disabled={
                              loading ||
                              !formData.virtual_relationships_is_enabled ||
                              formValues[connector.connector_code]
                                .virtual_relationships_exclusion_filters.disabled
                            }
                            label={
                              formValues[connector.connector_code]
                                .virtual_relationships_exclusion_filters.label
                            }
                            title={
                              formValues[connector.connector_code]
                                .virtual_relationships_exclusion_filters.tooltip
                            }
                            language="json"
                            showLineNumbers={false}
                            expandable={true}
                            placeholder={
                              formValues[connector.connector_code]
                                .virtual_relationships_exclusion_filters.placeholder
                            }
                            isOptional={true}
                            validation={
                              formErrors.virtual_relationships_exclusion_filters
                                ? {
                                    valid: false,
                                    message: formErrors.virtual_relationships_exclusion_filters,
                                  }
                                : undefined
                            }
                          />
                        </div>
                      )}
                  </React.Fragment>
                )}

                {formValues[connector.connector_code].attribute_matching_is_enabled.show && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <h4>Attribute Matching</h4>
                      <Checkbox
                        name={
                          formValues[connector.connector_code].attribute_matching_is_enabled
                            .dbFieldName
                        }
                        checked={formData.attribute_matching_is_enabled}
                        onChange={handleInputChange}
                        label={
                          formValues[connector.connector_code].attribute_matching_is_enabled.label
                        }
                        title={
                          formValues[connector.connector_code].attribute_matching_is_enabled.tooltip
                        }
                        disabled={
                          loading ||
                          formValues[connector.connector_code].attribute_matching_is_enabled
                            .disabled
                        }
                      />
                    </div>
                  </React.Fragment>
                )}
              </div>
            )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddConnectionForm = forwardRef(_AddConnectionForm);
